import { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useQuery, useMutation } from "@apollo/client";
import useAuth from "../../hooks/useAuth";
import useListStatuses from "../../hooks/useListStatuses";
import Search from "../../components/Search/Search";
import Filter from "../../components/Filter/Filter";
import Board from "../../components/Board/Board";
import AddWishListModal from "../../components/Board/AddWishListModal/AddWishListModal";
import Heading from "../../components/Heading/Heading";
import getPriorities from "../../graphql/getPriorities";
import updatePriority from "../../graphql/updatePriority";

const Box = styled.div`
  padding-right: 45px;
  margin-bottom: 44px;

  @media only screen and (max-width: 920px) {
    padding: 0 18px;
  }
`;

const Head = styled.div`
  margin-bottom: 35px;

  display: grid;
  grid-template-columns: repeat(2, max-content);
  grid-gap: 20px;
  align-items: center;

  @media only screen and (max-width: 920px) {
    margin-top: 10px;
  }

  @media only screen and (max-width: 600px) {
    margin-top: 0;
    grid-template-columns: 1fr;
    width: 100%;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: max-content 250px max-content;
  grid-gap: 20px;
  align-items: center;

  @media only screen and (max-width: 600px) {
    margin-top: 0;
    grid-template-columns: 1fr max-content;
    width: 100%;
  }
`;

const Button = styled.button`
  cursor: pointer;
  width: 36px;
  height: 36px;
  border: none;
  outline: none;
  border-radius: 50%;
  color: var(--color-text-secondary);
  background-color: var(--color-bg-sidebar);
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: var(--color-primary);
    color: var(--color-bg-main);
  }
`;

const buildFilter = (statuses) => {
  if (!statuses.length) return undefined;
  if (statuses.length === 1) return { status: { eq: statuses[0] } };
  return { or: statuses.map((s) => ({ status: { eq: s } })) };
};

const PrioritiesBoard = () => {
  const [isOpen, setOpen] = useState(false);
  const toggle = () => setOpen((open) => !open);
  const { selectedOrg } = useAuth();
  const listStatuses = useListStatuses();
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const context = { version: 4 };
  const filter = buildFilter(selectedStatuses.map((s) => s.value));
  const { data, loading, error, refetch } = useQuery(getPriorities, {
    variables: { organizationId: selectedOrg.value, filter },
    context,
  });
  const [updateCardStatus] = useMutation(updatePriority);
  const [priorities, setPriorities] = useState([]);
  const [searched, setSearched] = useState([]);
  const cards = searched.length > 0 ? searched : priorities;

  const statusOptions = [...listStatuses]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({ label: s.friendlyName, value: s.name }));

  useEffect(() => {
    if (data) setPriorities(data.listIssues.items);
  }, [data]);

  const handleSearch = (e) => {
    const filtered = priorities.filter((card) =>
      card.title.toLowerCase().includes(e.target.value.toLowerCase().trim())
    );

    setSearched(filtered);
  };

  const handleDragEnd = async (result) => {
    try {
      const { destination, source, draggableId } = result;

      // If user draggs a card and drops it outside of a column
      // or drops in the same column, do nothing
      if (!destination) return;
      if (destination.droppableId === source.droppableId)
        return showDndMessage();

      // Update local state
      const localPriorities = priorities.map((card) => {
        if (card.id !== draggableId) return card;
        return { ...card, status: destination.droppableId };
      });

      setPriorities(localPriorities);

      // Fetch after local is updated and replace local
      const variables = {
        id: draggableId,
        issue: { status: destination.droppableId },
      };

      await updateCardStatus({ variables, context });
      await refetch();
    } catch (err) {
      console.log(err);
    }
  };

  const showDndMessage = () =>
    toast.info(
      "Reordering is currently not available please reach out to a Caylent team member to change the order of priorities."
    );

  return (
    <Box>
      <Head>
        <Heading>Tasks</Heading>
        <Row>
          <Search
            className="priorities-board-search"
            placeholder="Search Tasks"
            handleChange={handleSearch}
          />
          <Filter
            handleFilter={(values) => setSelectedStatuses(values || [])}
            options={statusOptions}
            placeholder="All Statuses"
            isMulti
            value={selectedStatuses}
          />
          <Button className="priority-plus-button" onClick={toggle}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </Row>
      </Head>
      <Board
        priorities={cards}
        loading={loading}
        error={error}
        handleDragEnd={handleDragEnd}
      />
      {isOpen && <AddWishListModal isOpen={isOpen} toggle={toggle} />}
    </Box>
  );
};

export default PrioritiesBoard;
